import { useCopies } from "shared/hooks/useCopies";
import React from "react";
import { FormFieldOption } from "shared/form/fields/types";
import * as Yup from "yup";
import Validation from "shared/form/Validation";
import { CouponCodeFlowPropsType } from "./type";
import { useValidateInventoryCouponCode } from "./dataloader";
import { CouponCodeFormFieldsName } from "./enum";
import { BooleanOption } from "../../../../shared/enums/BooleanOption";

export default function useCouponCodeFlowService({
  onApplySuccessHandler,
  invalidCouponCodeHandler,
  noCouponCodeHandler,
  ...props
}: CouponCodeFlowPropsType) {
  const copies = useCopies();
  const formRef = React.useRef<any>();
  const { mutateAsync: validateCouponCode } = useValidateInventoryCouponCode(props.apiUrl);
  const haveCouponCodeOptions: Array<FormFieldOption> = React.useMemo(() => {
    return [
      { value: BooleanOption.YES, label: copies.get("yes") },
      { value: BooleanOption.NO, label: copies.get("no") }
    ];
  }, [copies]);

  const initialValues = React.useMemo(() => {
    return {
      [CouponCodeFormFieldsName.HAVE_COUPON_CODE]: props.couponCode
        ? BooleanOption.YES
        : props.purchaseOrderCreated
        ? BooleanOption.NO
        : "",
      [CouponCodeFormFieldsName.COUPON_CODE]: props.couponCode || ""
    };
  }, [props.couponCode, props.purchaseOrderCreated]);

  const submitHandler = React.useCallback(
    async values => {
      const couponCode = values[CouponCodeFormFieldsName.COUPON_CODE];
      try {
        const responseData = await validateCouponCode({
          params: { ...props.apiReqData, couponCode }
        });
        onApplySuccessHandler?.({ couponCode, responseData });
      } catch (e) {
        invalidCouponCodeHandler?.();
        formRef.current.setFieldTouched(CouponCodeFormFieldsName.COUPON_CODE, false);
        formRef.current.setFieldValue(CouponCodeFormFieldsName.COUPON_CODE, "");
      }
    },
    [onApplySuccessHandler, invalidCouponCodeHandler, validateCouponCode, props.apiReqData]
  );

  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      [CouponCodeFormFieldsName.HAVE_COUPON_CODE]: Validation.yupIsReq({
        required: true,
        copies: copies
      }),
      [CouponCodeFormFieldsName.COUPON_CODE]: Validation.textFieldValidation({
        required: true,
        copies: copies
      })
    });
  }, [copies]);

  const haveCouponCodeChangeHandler = React.useCallback(
    value => {
      if (value === BooleanOption.NO) {
        noCouponCodeHandler?.();
      }
    },
    [noCouponCodeHandler]
  );

  return {
    haveCouponCodeOptions,
    initialValues,
    submitHandler,
    validationSchema,
    haveCouponCodeChangeHandler,
    formRef
  };
}
